import type { IInfluencer } from '../redux/buzzbridge/types';
import { ASW_S3_LINK } from '../configurations/api/url';

/** Payload flag: attempt one social→Thspian copy during profile save (stripped before PATCH). */
export const PERSIST_SOCIAL_AVATAR_FLAG = '__persistSocialAvatar' as const;

export type InfluencerAvatarFields = Pick<IInfluencer, 'profile_picture' | 'instagram' | 'tiktok' | 'youtube'>;

export function hasStoredInfluencerProfilePicture(
    influencer: Pick<IInfluencer, 'profile_picture'> | null | undefined,
): boolean {
    return Boolean(influencer?.profile_picture?.trim());
}

/** Prefer Instagram, else TikTok — used when copying into Thspian storage. */
export function resolvePreferredSocialAvatarUrl(
    influencer: Pick<IInfluencer, 'instagram' | 'tiktok'> | null | undefined,
): string | undefined {
    const ig = influencer?.instagram?.profile_picture?.trim();
    if (ig) return ig;
    const tt = influencer?.tiktok?.profile_picture?.trim();
    if (tt) return tt;
    return undefined;
}

/**
 * Display URL: stored Thspian picture first, then IG → TikTok → YouTube.
 * Social URLs are preview-only and may expire.
 */
export function resolveInfluencerDisplayAvatarUrl(
    influencer: InfluencerAvatarFields | null | undefined,
): string | undefined {
    const stored = influencer?.profile_picture?.trim();
    if (stored) return stored;
    return resolvePreferredSocialAvatarUrl(influencer) || influencer?.youtube?.profile_picture?.trim() || undefined;
}

function isOurCdnUrl(url: string): boolean {
    const u = url.trim().toLowerCase();
    const markers = [ASW_S3_LINK, 'cloudfront.net', 'amazonaws.com'].filter(Boolean).map(s => String(s).toLowerCase());
    return markers.some(m => m && u.includes(m.replace(/^https?:\/\//, '').replace(/\/$/, '')));
}

/** True for remote IG/TikTok (etc.) avatar URLs that should not be PATCHed as profile_picture. */
export function isEphemeralSocialAvatarUrl(
    url: string | null | undefined,
    influencer?: Pick<IInfluencer, 'instagram' | 'tiktok' | 'youtube'> | null,
): boolean {
    const trimmed = url?.trim();
    if (!trimmed) return false;
    if (!/^https?:\/\//i.test(trimmed)) return false;
    if (isOurCdnUrl(trimmed)) return false;

    const linked = [
        influencer?.instagram?.profile_picture,
        influencer?.tiktok?.profile_picture,
        influencer?.youtube?.profile_picture,
    ]
        .map(s => s?.trim())
        .filter(Boolean) as string[];
    if (linked.includes(trimmed)) return true;

    const u = trimmed.toLowerCase();
    return (
        u.includes('cdninstagram.com') ||
        u.includes('fbcdn.net') ||
        u.includes('instagram.') ||
        u.includes('tiktokcdn') ||
        u.includes('tiktok.com') ||
        u.includes('muscdn.com') ||
        u.includes('ttlivecdn') ||
        u.includes('googleusercontent.com')
    );
}

/** Browser fetch of social CDNs often fails CORS — returns null when blocked. */
export async function fetchRemoteAvatarAsFile(url: string): Promise<File | null> {
    try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const blob = await res.blob();
        if (!blob.size) return null;
        const type = blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
        return new File([blob], `social-avatar.${ext}`, { type });
    } catch {
        return null;
    }
}

export function shouldAttemptSocialAvatarPersist(
    influencer: InfluencerAvatarFields | null | undefined,
    options?: { hasOwnUpload?: boolean },
): boolean {
    if (options?.hasOwnUpload) return false;
    if (hasStoredInfluencerProfilePicture(influencer)) return false;
    return Boolean(resolvePreferredSocialAvatarUrl(influencer));
}
